import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { customerService } from '../services/customerService';

export function useCustomer(customerId: string | null) {
  const queryClient = useQueryClient();

  const { data: customer, isLoading: isLoadingCustomer } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => customerService.getCustomer(customerId!),
    enabled: !!customerId,
  });

  const { data: orders = [], isLoading: isLoadingOrders } = useQuery({
    queryKey: ['customer-orders', customerId],
    queryFn: () => customerService.getCustomerOrders(customerId!),
    enabled: !!customerId,
  });

  const { data: notes = [], isLoading: isLoadingNotes } = useQuery({
    queryKey: ['customer-notes', customerId],
    queryFn: () => customerService.getCustomerNotes(customerId!),
    enabled: !!customerId,
  });

  const addNoteMutation = useMutation({
    mutationFn: (content: string) => customerService.addNote(customerId!, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-notes', customerId] });
      toast.success('Note added');
    },
  });

  const createOrderMutation = useMutation({
    mutationFn: (data: { lineItems: { variant_id: string; quantity: number }[]; note?: string }) =>
      customerService.createOrder(customerId!, data.lineItems, data.note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-orders', customerId] });
    },
    onError: () => {
      toast.error('Failed to create order');
    },
  });

  return {
    customer,
    orders,
    notes,
    isLoading: isLoadingCustomer || isLoadingOrders || isLoadingNotes,
    addNote: addNoteMutation.mutate,
    createOrder: createOrderMutation.mutateAsync,
    isCreatingOrder: createOrderMutation.isPending,
  };
}
